<template>

    <div :class="{'pr-6': windowWidth < (visibleFilters ? (visibleUsers ? 1586 : 1506) : (visibleUsers ? 1506 : 1330) )}" :style="`min-width: ${ visibleFilters ? (visibleUsers ? '96' : '91') : (visibleUsers ? '91' : '80') }rem;`">

        <!-- TOP : ActionBar -->
        <ActionBar 
            :numberOfFilters="numberOfFilters" :visibleFilters="visibleFilters" @toggle-show-filters="visibleFilters = !visibleFilters" @search-value-changed="(val) => searchValue=val"
            :selectedLicenses="selectedLicenses" @refresh-licenses-table="$emit('refresh-licenses-table');"
            :userFilterOn="userFilterOn" :selectedUser="selectedUser" :visibleUsers="visibleUsers" @toggle-show-users="visibleUsers = !visibleUsers"
        />
        <div class="flex align-items-start flex-wrap gap-3">

            <!-- LEFT : Users block -->
            <transition name="transition-filters" mode="in-out">
                <div v-if="visibleFilters" style="flex: 1; border-radius: var(--my-border-radius); min-height: 37rem; height: 37rem;  min-width: 16rem; width:20vw; max-width:16rem;">
                    <Filters
                        v-model:selectionLicensesAdmin="selectionLicensesAdmin"
                        v-model:selectionLicensesActivated="selectionLicensesActivated"
                        v-model:selectionLicensesApplication="selectionLicensesApplication"
                        v-model:selectionLicensesActiveUser="selectionLicensesActiveUser"
                        v-model:selectionLicensesHardware="selectionLicensesHardware"
                        v-model:numberOfFilters="numberOfFilters"
                    />
                </div>
            </transition>

            <!-- MIDDLE : Datatable -->
            <div class="licenses flex flex-column w-full" style="flex: 5; transition: all 0.5s ease;">
                <div ref="licensesDatatable" class="datatable">
                    <LicensesTable
                        :licenses="licenses"
                        :licensesToShow="licensesToShow"
                        :licensesDatatableHeight="licensesDatatableHeight"
                        :selectedUser="selectedUser"
                        @selected-user-changed="(val) => selectedUser = val"
                        @selected-licenses-changed="(val) => selectedLicenses = val"
                        @refresh-licenses-table="$emit('refresh-licenses-table');"
                    />
                </div>
            </div>

            <!-- RIGHT : Users block -->
            <transition name="transition-users" mode="in-out">
                <div v-if="visibleUsers" style="flex: 1; border-radius: var(--my-border-radius); min-height: 20rem; min-width: 18rem; max-width: 26rem;" :style="`height: ${licensesDatatableHeight}px; max-height: ${licensesDatatableHeight}px;`">
                    <Users
                        :selectedUser="selectedUser" v-model:filterOn="userFilterOn" @clicked-user="(val) => selectedUser = (selectedUser && selectedUser.id === val.id) ? null : val"
                    />
                </div>
            </transition>
        </div>   
        
    </div> 
</template>

<script setup>
// --- IMPORTS ---
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
// vue
import Filters from './Sides/Filters/Filters.vue';
import Users from './Sides/Users/Users.vue';
import ActionBar from './Elements/ActionsBar.vue'
import LicensesTable from './Elements/LicensesTable.vue'
// Others
import { useStore } from 'vuex';

const store = useStore()

// --- User info ---
const user = computed(() => store.state.user.user)

// --- EMITS ---
const emit = defineEmits(['refresh-licenses-table'])

// --- Licenses ---
const licenses = computed(() => store.state.licenses.licenses)

// --- Filters block variables ---
const visibleFilters = ref(false)
const numberOfFilters = ref(0)

// --- Users block variables ---
const visibleUsers = ref(false)
const selectedUser = ref(null)
const userFilterOn = ref(false)


// --- Filtering ---

// Searching Field
const searchValue = ref('')

// Advanced Filters
const selectionLicensesAdmin = ref('All')
const selectionLicensesActivated = ref('All')
const selectionLicensesApplication = ref('None')
const selectionLicensesActiveUser = ref({'first_name': '', 'last_name': '', 'email': ''})
const selectionLicensesHardware = ref({'label': '', 'serial': ''})

// Filtering process
const licensesToShow = computed(() => {
    if (!selectionLicensesAdmin.value) {selectionLicensesAdmin.value = "All"}
    if (!selectionLicensesActivated.value) {selectionLicensesActivated.value = "All"}
    // Check special cases
    if (selectionLicensesActivated.value === "Available") {selectionLicensesActiveUser.value = {'first_name': '', 'last_name': '', 'email': ''}; selectionLicensesHardware.value = {'label': '', 'serial': ''}}
    // Filter 0 : Search Field
    const filterSearch = (inputLicenses) => {
        if (searchValue.value === '') { return inputLicenses }
        else { 
            // Make a "string of interest" for each license (a string that concat all the string we want to check), and then check on that global string if the searched string is included or not
            // Map each license into its "string of interest"
            const stringsOfInterest = inputLicenses.map((license) => {
                let res = ""
                res = res + license.serial_number // Serial number
                res = res + license.product.application // Application
                // res = res + license.product.version // Version
                res = res + license.product.options.map(option => option.label + option.abbrev).join('') // Options
                res = res + license.product.channels // Channels
                res = res + license.product.type // Type
                res = res + (license.product.exp !== '-1' ? license.product.exp : 'No Expiration') // Exp
                res = res + license.admin.first_name + license.admin.last_name + license.admin.email // Admin
                res = res + license.delegates.map(delegate => delegate.first_name + delegate.last_name + delegate.email).join('') // Delegates
                if (license.current_status.activated) {
                    // if (license.current_status.activeUser) { res = res + license.current_status.activeUser.username + license.current_status.activeUser.email } // Active user
                    if (license.current_status.activeHardware) { res = res + license.current_status.activeHardware.first_name + license.current_status.activeHardware.last_name + license.current_status.activeHardware.serial } // Active hardware
                    // if (license.current_status.activationTimestamp) { res = res + format(new Date(license.current_status.activationTimestamp*1000), 'MMM do, yyyy, HH:mm') } // Activation date
                }
                return res.toLowerCase()
            })
            return inputLicenses.filter((license, index) => stringsOfInterest[index].includes(searchValue.value.toLowerCase()))
        }
    }
    // Filter 1 : Admin ?
    const filterAdmin = (inputLicenses) => {
        if (selectionLicensesAdmin.value === 'All') { return inputLicenses }
        else if (selectionLicensesAdmin.value === 'Me') { return inputLicenses.filter((license) => license.admin.id === user.value.id) }
        else if (selectionLicensesAdmin.value === 'Others') { return inputLicenses.filter((license) => license.admin.id !== user.value.id) }
        return inputLicenses
    }
    // Filter 2 : Activated
    const filterActivated = (inputLicenses) => {
        if (selectionLicensesActivated.value === 'All') { return inputLicenses }
        else if (selectionLicensesActivated.value === 'In Use') { return inputLicenses.filter((license) => license.current_status.activated === true) }
        else if (selectionLicensesActivated.value === 'Available') { return inputLicenses.filter((license) => license.current_status.activated === false) }
        return inputLicenses
    }
    // Filter 3 : Application
    const filterApplication = (inputLicenses) => {
        if (selectionLicensesApplication.value === 'None') { return inputLicenses }
        else { return inputLicenses.filter((license) => license.product.application === selectionLicensesApplication.value ) }
    }
    // Filter 4 : Active user
    const filterActiveUser = (inputLicenses) => {
        if (!selectionLicensesActiveUser.value || !selectionLicensesActiveUser.value.email.length>0) { return inputLicenses }
        else {
            return inputLicenses.filter((license) => (license.current_status.activated === true) && license.current_status.activeUser && (license.current_status.activeUser.id ?? "-1") === selectionLicensesActiveUser.value.id )
        }
    }
    // Filter 5 : Hardware
    const filterHardware = (inputLicenses) => {
        if (!selectionLicensesHardware.value || !selectionLicensesHardware.value.serial.length>0) { return inputLicenses }
        else { return inputLicenses.filter((license) => (license.current_status.activated === true) && license.current_status.activeHardware && (license.current_status.activeHardware.serial ?? "-1") === selectionLicensesHardware.value.serial) }
    }
    // Filter 6 : All users filter
    const filterAllUsers = (inputLicenses) => {
        if (!userFilterOn.value || selectedUser.value===null) { return inputLicenses }
        else { return inputLicenses.filter((license) => (license.admin.id === selectedUser.value.id) || (license.delegates.some(delegate => delegate.id === selectedUser.value.id)) ) }
    }
    // Filter processing
    return filterAllUsers(filterHardware(filterActiveUser(filterApplication(filterActivated(filterAdmin(filterSearch(licenses.value)))))))
})

// --- Actions selected licences ---
const selectedLicenses = ref([])

// --- Height managing ---
const windowHeight = ref(0)
const getWindowHeight = () => {
  windowHeight.value = window.innerHeight;
};
const licensesDatatable = ref(null)
const licensesDatatableTopPosY = ref(0);
const getElementPosition = () => {
  const elementRect = licensesDatatable.value.getBoundingClientRect();
  licensesDatatableTopPosY.value = elementRect.top;
};
onMounted(() => {
  getWindowHeight();
  getElementPosition();
  window.addEventListener('resize', getWindowHeight);
  window.addEventListener('resize', getElementPosition);
});
onBeforeUnmount(() => {
  window.removeEventListener('resize', getWindowHeight);
  window.removeEventListener('resize', getElementPosition);
});
const licensesDatatableHeight = computed(() => windowHeight.value - licensesDatatableTopPosY.value - 20)

// --- Resize issue ---
const windowWidth = ref(window.innerWidth);
const updateWindowWidth = () => {
    windowWidth.value = window.innerWidth;
};
onMounted(() => {
  window.addEventListener('resize', updateWindowWidth);
});
onBeforeUnmount(() => {
  window.removeEventListener('resize', updateWindowWidth);
});
</script>

<style scoped>

/* TRANSITIONS */
.transition-users-enter-from {
  transform: translate(100%,0%);
  opacity: 0;
}
.transition-users-leave-to {
  transform: translate(100%,0%);
  opacity: 0;
}
.transition-users-enter-active {
  transition: 0.25s ease;
}
.transition-users-leave-active {
  transition: 0.2s ease;
}
.transition-filters-enter-from {
  transform: translate(-100%,0%);
  opacity: 0;
}
.transition-filters-leave-to {
  transform: translate(-100%,0%);
  opacity: 0;
}
.transition-filters-enter-active {
  transition: 0.25s ease;
}
.transition-filters-leave-active {
  transition: 0.2s ease;
}
</style>